/* eslint-disable no-param-reassign */
import axios, { AxiosError, AxiosResponse } from 'axios'

import { getLocalStorageOnboardingFormToken } from '../utils/localStorage'

import { log } from './log'
import { getSession, removeSession } from './session'

const url = process.env.REACT_APP_BFB_API
const url_new = process.env.REACT_APP_BFB_API_NEW

// Set config defaults when creating the instance
const instance = axios.create({
  baseURL: url
})

// Add a request interceptor
instance.interceptors.request.use((config) => {
  const authHeader = config.headers.getAuthorization()

  if (!authHeader) {
    config.headers.setAuthorization('X-BAREKSA-WEB')
    config.headers.setContentType('application/json')
  }

  const session = getSession('b-bfb')

  if (session) {
    config.headers.set('X-TOKEN-CLIENT', session.local_token as string)
    config.headers.set('X-BAREKSA-USER', session.id as string)
    config.headers.set('X-BAREKSA-INVESTOR', session.investor_id as string)
  }

  if (config.useNewAPI) {
    config.baseURL = url_new
  }

  if (config.useOnboardingFormToken) {
    const onboardingFormToken = getLocalStorageOnboardingFormToken()
    if (onboardingFormToken) {
      config.headers.setAuthorization(onboardingFormToken)
    }
  }

  return config
})

type ApiErrorData = {
  code?: number
  message?: string
  data?: { message?: string }
}

// Add a response interceptor
instance.interceptors.response.use(
  (response: AxiosResponse) =>
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    response,
  (error: AxiosError<ApiErrorData>) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // error response for unauthrorized
    const { response } = error
    const request = error.request as
      | { response?: { data?: ApiErrorData } }
      | undefined

    const responseMessage =
      error.response?.data?.data?.message || error.response?.data?.message
    const requestMessage =
      request?.response?.data?.data?.message || request?.response?.data?.message
    const errorMessage = responseMessage || requestMessage || error.message
    const requestData = error.response?.config
    const responseData = error.response?.data || request?.response

    if (error.response?.status) {
      const requestUrl = (error.request as XMLHttpRequest | undefined)
        ?.responseURL

      log({
        errorType: 'error_response_api',
        message: errorMessage,
        description: errorMessage,
        payload: {
          apiUrl: error.response?.config?.url || requestUrl,
          request: {
            headers: requestData?.headers,
            method: requestData?.method,
            body: requestData?.data || {},
            url: requestData?.url
          },
          response: responseData
        }
      })
    }

    if (response && (response.status === 401 || response.data?.code === 4401)) {
      if (location.pathname !== '/') {
        window.location.href = '/'
        removeSession('b-bfb')
        removeSession('b-bfb-login')
        removeSession('b-bfb-ph')
        removeSession('otp-attm')
        sessionStorage.removeItem('b-bfb-cd')
      }
    }

    return Promise.reject(error)
  }
)

export default instance
